<template>
    <brick-settings-popup
        :resources="{
            deletePromptTitle: resource('JourneyBuilder_GoalSettings_Delete_Title'),
            deletePromptBody: resource('JourneyBuilder_GoalSettings_Delete_Body'),
            brickTitle: title
        }"
        :brick="brick"
        :name.sync="newName"
        :invalid="$v.$invalid || isLoading || !customConditionsValid"

        @save="onSave()"
        @delete="$emit('delete')"
        @hidden="$emit('hidden')"
        @display-errors="displayErrorMessages()"
    >
        <settings-item icon="fas fa-plane-arrival" :label="resource('JourneyBuilder_GoalSettings_GoalType_Label')">
            <template #input="{id}">
                <v-select class='form-control'
                          v-model="goalTypeModel"

                          :input-id="id"
                          :options="selectableGoalTypes"
                          :disabled="!canWrite || selectableGoalTypes.length === 0"
                          :clearable="false"

                          searchable
                          filterable
                          taggable

                          close-on-select
                >
                    <div slot='no-options' />
                </v-select>
            </template>
            <template #errors>
                <required-error-message v-if="$v.goalTypeModel.$dirty && $v.goalTypeModel.$invalid" :field-name="resource('JourneyBuilder_GoalSettings_GoalType_Label')" />
            </template>
        </settings-item>
        
        <settings-item :label="resource('JourneyBuilder_GoalSettings_DeleteRecords_Label')">
            <template #input>
                <toggle-button 
                    v-model="settings.deleteRecords"
                    
                    :sync="true"
                    :labels="{checked: resource('Bool_Yes'), unchecked: resource('Bool_No')}"
                    :width="78"
                />
            </template>
        </settings-item>

        <template v-if="supportsDeleteAfter">
            <div>
                <label class="control-label">
                    {{resource('JourneyBuilder_GoalSettings_DeleteAfter_Label')}}

                    <help-popover :content="resource('JourneyBuilder_GoalSettings_DeleteAfter_Tooltip')" />
                </label>
            </div>

            <jira-style-time-period v-model="settings.deleteAfter" :disabled="!canWrite" />
        </template>
        
        <div>
            <settings-item :label="resource('JourneyBuilder_GoalSettings_AudienceSettings_Title')">
                <template #input>
                    <toggle-button
                        v-model="displayAudienceSettings"

                        :sync="true"
                        :labels="{checked: resource('Bool_Yes'), unchecked: resource('Bool_No')}"
                        :width="78"
                    />
                </template>
            </settings-item>

            <goal-audience-settings
                v-if="displayAudienceSettings"
                
                :is-new-audience.sync="settings.audience.isNew"
                :new-audience-name.sync="settings.audience.newAudienceName"
                :audience-id.sync="settings.audience.audienceId"
            />
        </div>
        
        <goal-custom-conditions
            ref="customConditions"
            
            v-model="settings.customConditions"
            
            :goal-type="goalTypeModel && goalTypeModel.id"

            @loading-state-changed="isLoading = $event"
            @validation-state-changed="customConditionsValid = $event"
        />
    </brick-settings-popup>
</template>

<script>
import api from "@modules/JourneyBuilder/api";
import { brickDefinitions } from "@modules/JourneyBuilder/Bricks/brick-definitions";
import { goalTypes } from "@modules/JourneyBuilder/Bricks/brick-types";
import { required } from 'vuelidate/lib/validators'
import { bootstrapDangerGrowl } from "@core/Util/growler";
import { prompt } from "@modules/JourneyBuilder/Util/prompt";
import resource from "@core/Util/resources";
import sprintf from 'sprintf';
import moment from 'moment';

import resourceMixin from '@core/Util/Mixins/resources';
import brickSettingsMixin from '../Mixins/brick-settings'
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
import { validationMixin } from 'vuelidate';

import BrickSettingsPopup from '../BrickSettingsPopup';
import GoalCustomConditions from './GoalCustomConditions';
import GoalAudienceSettings from "./GoalAudienceSettings";
import InputGroup from '@core/Components/Inputs/InputGroup';
import RequiredErrorMessage from "@core/Components/Errors/RequiredErrorMessage";
import VSelect from 'vue-select';
import { ToggleButton } from 'vue-js-toggle-button';
import HelpPopover from "@modules/JourneyBuilder/Components/Elements/HelpPopover";
import JiraStyleTimePeriod from "@core/Components/Inputs/JiraStyleTimePeriod";

/**
 * Verify whether the two given audience settings objects are considered equal
 * @param audienceSettingsLeft
 * @param audienceSettingsRight
 * @returns {boolean}
 */
function audienceSettingsAreEqual(audienceSettingsLeft, audienceSettingsRight) {
    //Account for one/both being falseys
    if(!audienceSettingsLeft || !audienceSettingsRight) {
        return audienceSettingsLeft === audienceSettingsRight;
    }

    //If both versions are objects, perform a diff on the relevant properties used to determine how to handle the audience
    return audienceSettingsLeft.audienceId === audienceSettingsRight.audienceId &&
        audienceSettingsLeft.newAudienceName === audienceSettingsRight.newAudienceName &&
        audienceSettingsLeft.isNew === audienceSettingsRight.isNew;
}

const BUTTONS = {
    CANCEL: {
        get name() { return resource('JourneyBuilder_GoalSettings_AudienceChangedPrompt_DontSave'); },
        emit: 'cancel',
        icon: 'fa fa-trash',
        class: 'btn btn-danger'
    },
    OK: {
        get name() { return resource('Button_OK') },
        emit: 'ok',
        class: 'btn btn-outline-primary'
    },
    NOW: {
        get name() { return resource('JourneyBuilder_GoalSettings_AudienceChangedPrompt_StartNow'); },
        emit: 'now',
        class: 'btn btn-outline-primary'
    },
    RETROACTIVE: {
        get name() { return resource('JourneyBuilder_GoalSettings_AudienceChangedPrompt_StartRetroactively') },
        emit: 'retroactive',
        class: 'btn btn-outline-primary'
    }
}

export default {
    name: "GoalBrickSettings",
    
    components: {
        RequiredErrorMessage,
        BrickSettingsPopup,
        GoalCustomConditions,
        GoalAudienceSettings,
        InputGroup,
        VSelect,
        ToggleButton,
        HelpPopover,
        JiraStyleTimePeriod
    },
    
    mixins: [resourceMixin, brickSettingsMixin, permissionsMixin, validationMixin],
    
    validations: {
        goalTypeModel: {
            required
        }
    },
    
    data(){
        return {
            goalTypes: [],
            touchpointTypes: [],
            audiences: [],

            isLoading: true,
            customConditionsValid: true,
            
            initialAudienceSettings: undefined
        }
    },
    
    computed: {
        /**
         * https://datanext.atlassian.net/browse/DPN-2845
         * Generate a default name for the brick based on its configuration
         *
         * @return {String} default name
         */
        defaultName(){
            if(this.goalTypeModel){
                return this.goalTypeModel.label;
            }

            return brickDefinitions[this.brickType].displayName;
        },
        
        displayAudienceSettings: {
            get() {
                return !!this.settings.audience;
            },
            set(value) {
                if(!value) {
                    this.settings.audience = undefined;
                } else {
                    //Ensure reactivity for all our properties
                    this.settings.audience = {};
                    this.$set(this.settings.audience, 'isNew', false); 
                    this.$set(this.settings.audience, 'newAudienceName', undefined); 
                    this.$set(this.settings.audience, 'audienceId', undefined); 
                }
            }
        },

        /**
         * @returns {JourneyBrick[]}
         */
        sourceBricks(){
            return this.brick.incomingConnections.map(connection => connection.sourceBrick);
        },
        
        sourceBrickTypes() {
            return this.sourceBricks.reduce((types, brick) => {
                if(brick.config && brick.config.touchpointParentType) {
                    types.push(brick.config.touchpointParentType);
                }
                
                return types;
            }, []);
        },
        
        title(){
            if(this.goalTypeModel){
                return sprintf(this.resource('JourneyBuilder_GoalSettings_Title'), this.goalTypeModel.label);
            }
            
            return '';
        },
        
        selectableGoalTypes() {
            return this.goalTypes.filter(gT => gT.touchPointParentType === null || this.sourceBrickTypes.includes(gT.touchPointParentType));
        },
        
        goalTypeModel:{
            get() {
                return this.selectableGoalTypes.find(gT => gT.id === this.settings.goalType);
            },
            set(value){
                this.$v.goalTypeModel.$touch();
                this.settings.goalType = value && value.id;
            }
        },

        supportsDeleteAfter() {
            //Only support Conversion Goal Type
            //https://datanext.atlassian.net/browse/DPN-3245
            return this.settings.goalType === goalTypes.Conversie;
        }
    },
    
    watch: {
        defaultName(newDefaultName, oldDefaultName){
            //Update our name to the new default when the user hasn't entered a name themselves
            if(this.newName === oldDefaultName){
                this.newName = newDefaultName;
            }
        },

        supportsDeleteAfter(value) {
            //Reset value upon deactivation
            if(!value) {
                this.settings.deleteAfter = undefined;   
            }
        }
    },
    
    async created() {
        this.settings = {
            //Preserve values set from external locations
            ...this.settings,

            //Explicitly declare values which we'll be modifying
            goalType: this.brick.config.goalType,
            customConditions: this.brick.config.customConditions || [],
            deleteRecords: this.brick.config.deleteRecords || false,
            deleteAfter: this.brick.config.deleteAfter || undefined,
            audience: this.brick.config.audience && { ...this.brick.config.audience }, //Assume all properties we will be modifying are present for now
            goal: true //Potentially used in backend by DSS
        };
        
        if(this.settings.audience) {
            //Shallow copy - audience object only has top level fields
            this.initialAudienceSettings = {
                ...this.settings.audience
            };
        }
        
        try {
            this.goalTypes = await api.getGoalTypes();
            this.touchpointTypes = await api.getTouchpointTypes();   
            this.audiences = await api.getAudiences();
        } catch (e) {
           bootstrapDangerGrowl(e); 
        }
    },

    methods: {
        async onSave() {
            //If our audience was modified we need explicit confirmation prior to saving
            if(
                !audienceSettingsAreEqual(this.initialAudienceSettings, this.settings.audience)
            ) {
                let selectedOption;

                try {
                    //If our audiences aren't equal, and our new audience is a falsey.. our audience option has been disabled
                    const wasDisabled = !this.settings.audience;
                    
                    selectedOption = await (wasDisabled ? this.promptAudienceDisabled() : this.promptAudienceChanged());
                } catch(e) {
                    //Prevent saving upon closing the popup - the user should make an explicit choice
                    return;
                }

                const now = moment.utc().toISOString();
                
                if(selectedOption === BUTTONS.CANCEL.emit) {
                    this.settings.audience = this.initialAudienceSettings;
                } else if (selectedOption === BUTTONS.OK.emit) {
                    //https://datanext.atlassian.net/browse/DPN-3349
                    //Simply clicking OK in our 'disabled' prompt should not include the 'applyRetroactively' option
                    this.settings.audienceChanged = {
                        date: now
                    }
                } else {
                    this.settings.audienceChanged = {
                        date: now,
                        applyRetroactively: selectedOption === BUTTONS.RETROACTIVE.emit
                    }
                }
            }
            
            this.saveSettings();
        },
        
        async promptAudienceDisabled() {
            const { isNew, newAudienceName, audienceId } = this.initialAudienceSettings;
            
            let audienceName = '';
            if(isNew) {
                audienceName = newAudienceName;
            } else {
                //Our selected audience may not exist anymore
                const currentAudience = this.audiences.find(a => a.id === audienceId);
                if(currentAudience) {
                    audienceName = currentAudience.name;
                }
            }
            
            //Put audienceName in quotes/add spaces as to clearly display it to the user
            if(audienceName) audienceName = ` "${audienceName}" `;
            
            return prompt(
                this.resource('JourneyBuilder_GoalSettings_AudienceChangedPrompt_Title'),
                sprintf(this.resource('JourneyBuilder_GoalSettings_AudienceChangedPrompt_Disabled_Body'), audienceName),
                {
                    buttons: [BUTTONS.CANCEL, BUTTONS.OK]
                }
            );
        },
        
        promptAudienceChanged() {
            return prompt(
                this.resource('JourneyBuilder_GoalSettings_AudienceChangedPrompt_Title'),
                this.resource('JourneyBuilder_GoalSettings_AudienceChangedPrompt_Body'),
                {
                    buttons: [BUTTONS.CANCEL, BUTTONS.NOW, BUTTONS.RETROACTIVE]
                }
            )
        },
        
        displayErrorMessages(){
            this.$v.$touch();
            
            if(this.$refs.customConditions) {
                this.$refs.customConditions.triggerValidations();
            }
        }
    }
}
</script>
